import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Truck, Wrench, Package, HardHat, LogOut, HardDrive, Users as UsersIcon, ScrollText, MapPinned, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Özet Panosu", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/machines", label: "Makine, Araç & Ekipman", icon: Truck, testid: "nav-machines" },
  { to: "/maintenance", label: "Bakım / Onarım", icon: Wrench, testid: "nav-maintenance" },
  { to: "/stock", label: "Stok / Malzeme", icon: Package, testid: "nav-stock" },
  { to: "/equipment", label: "Ekipman Zimmet", icon: HardHat, testid: "nav-equipment" },
  { to: "/locations", label: "Saha Sorgusu", icon: MapPinned, testid: "nav-locations" },
];

const ADMIN_NAV = [
  { to: "/audit", label: "İşlem Kaydı", icon: ScrollText, testid: "nav-audit" },
  { to: "/users", label: "Kullanıcılar", icon: UsersIcon, testid: "nav-users" },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? [...NAV, ...ADMIN_NAV] : NAV;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile backdrop */}
      {open && (
        <div data-testid="sidebar-backdrop" onClick={() => setOpen(false)} className="fixed inset-0 z-40 bg-black/50 lg:hidden" />
      )}

      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        className={`w-64 shrink-0 bg-zinc-950 text-zinc-50 flex flex-col fixed h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-6 py-6 border-b border-zinc-800 flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-sm flex items-center justify-center shrink-0 overflow-hidden p-0.5">
            <img src="/rayem-logo.png" alt="RAYEM" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-black text-sm leading-none tracking-tight">RAYEM MAKİNE &amp; STOK TAKİP</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-1">Saha Operasyon</div>
          </div>
          <button data-testid="sidebar-close" onClick={() => setOpen(false)} className="ml-auto lg:hidden text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-primary text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-sm object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-sm bg-zinc-800 flex items-center justify-center text-xs font-bold shrink-0">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-zinc-400 hover:bg-zinc-900 hover:text-red-400 transition-colors duration-200"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        <header style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }} className="bg-white border-b border-border px-4 sm:px-6 lg:px-8 pb-4 lg:pb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="sidebar-toggle" onClick={() => setOpen(true)} className="lg:hidden shrink-0 text-zinc-700 hover:text-primary">
              <Menu className="w-6 h-6" />
            </button>
            <div className="min-w-0">
              <h1 className="font-display font-black text-xl sm:text-2xl lg:text-3xl tracking-tight leading-none text-zinc-950 truncate">
                {title}
              </h1>
              {subtitle && <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2 sm:justify-end">{actions}</div>}
        </header>
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
